import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Box, Button, Typography } from '@mui/material';
import type { CopyButtonProps } from './types';
import type { CopyStatus } from './useCopyToClipboard';
import { useCopyToClipboard } from './useCopyToClipboard';

const STATUS_TEXT: Record<CopyStatus, string> = {
  idle: '',
  copied: 'Copied',
  failed: 'Copy failed',
};

/**
 * A labelled button that copies text, and says whether it worked.
 *
 * **The result is a live region beside the button, not a change to its label.**
 * A label that flips to "Copied" changes the button's accessible name under the
 * person who just pressed it; a status next to it is announced and leaves the
 * control as it was.
 */
export const CopyButton = ({ text, label, onCopy }: CopyButtonProps) => {
  const { status, copy } = useCopyToClipboard(text, onCopy);

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
      <Button
        variant="outlined"
        size="small"
        onClick={copy}
        startIcon={<ContentCopyIcon aria-hidden fontSize="small" />}
      >
        {label}
      </Button>

      <Typography
        variant="body2"
        component="span"
        role="status"
        sx={{ fontSize: 12, color: 'text.secondary' }}
      >
        {STATUS_TEXT[status]}
      </Typography>
    </Box>
  );
};
